import type * as THREE from 'three';
import type { TouchField } from '../core/input/TouchField';
import type { Quality } from '../core/util/quality';
import type { PRNG } from '../core/util/prng';
import type { IdleTimer } from '../core/kiosk/idle';

/** Size in CSS pixels, plus the effective device-pixel-ratio in use. */
export interface SketchSize {
  w: number;
  h: number;
  dpr: number;
}

/** Everything a sketch is handed at init. */
export interface SketchContext {
  renderer: THREE.WebGLRenderer;
  input: TouchField;
  idle: IdleTimer;
  quality: Quality;
  rng: PRNG;
  size: SketchSize;
}

/** The contract every sketch implements. */
export interface Sketch {
  init(ctx: SketchContext): void | Promise<void>;
  /** Advance simulation/logic. dt and t are in seconds. */
  update(dt: number, t: number): void;
  /** Draw the frame. */
  render(): void;
  /** Viewport changed (CSS pixels). */
  resize(w: number, h: number): void;
  /** Free ALL GPU resources. Called on sketch switch. */
  dispose(): void;
}

export type SketchFactory = () => Sketch;

export type SketchTag =
  | 'dimensionality'
  | 'figure-ground'
  | 'observer'
  | 'self'
  | 'perception'
  | 'time';

/** Top-level grouping shown as a section in the menu. */
export type SketchCategory =
  | 'cosmos'
  | 'waves'
  | 'materials'
  | 'geometry'
  | 'life'
  | 'flow'
  | 'perception'
  | 'mind';

export interface CategoryDef {
  id: SketchCategory;
  label: string;
  /** Short label for the jump-nav. */
  short: string;
  blurb: string;
  /** Accent colour for this category's section + cards. */
  accent: string;
}

/** Ordered categories with their menu headings and accent colours. */
export const CATEGORIES: CategoryDef[] = [
  { id: 'cosmos', label: 'Cosmos', short: 'Cosmos', blurb: 'Gravity, relativity and the architecture of the night sky', accent: '#ffb24a' },
  { id: 'waves', label: 'Waves, Light & Quanta', short: 'Waves', blurb: 'Wavefunctions, standing waves, optics and light', accent: '#3aa0ff' },
  { id: 'materials', label: 'Materials', short: 'Materials', blurb: 'The science of matter — crystals, tilings, magnetism and phase transitions', accent: '#9aa7c7' },
  { id: 'geometry', label: 'Geometry & Dimension', short: 'Geometry', blurb: 'Non-Euclidean space, higher dimensions, strange attractors — math made visible', accent: '#4fd2ff' },
  { id: 'life', label: 'Life & Emergence', short: 'Life', blurb: 'Automata, agents and growth that act alive', accent: '#5bd97a' },
  { id: 'flow', label: 'Flow & Fluids', short: 'Flow', blurb: 'Fluids, particles, recursion and psychedelia', accent: '#9b7bff' },
  { id: 'perception', label: 'Perception', short: 'Perception', blurb: 'The psychophysics of seeing — illusions that catch perception in the act', accent: '#ff5b9e' },
  { id: 'mind', label: 'Mind & Self', short: 'Mind', blurb: 'Attention, self-recognition and consciousness — the observer turned subject', accent: '#ff7a6b' },
];

/** Menu-facing metadata plus a lazy loader for the sketch module. */
export interface SketchMeta {
  id: string;
  title: string;
  blurb: string;
  category: SketchCategory;
  tags: SketchTag[];
  needs?: ('camera' | 'mic')[];
  /** Performance warnings, shown as "high gpu" / "high cpu" chips on the card. */
  perf?: ('gpu' | 'cpu')[];
  /** Optional "Inspired by <author>" attribution shown on the menu card. */
  credit?: { author: string; url: string };
  /** Bundled preview-thumbnail URL (from the sketch folder's thumb.jpg), if any. */
  thumb?: string;
  /** A "Sandbox" sketch: an in-progress/incomplete/shelved sketch, hidden unless
   *  the Sandbox toggle is on. Set by a `"sandbox": true` key in the sketch meta. */
  sandbox?: boolean;
  load: () => Promise<{ default: SketchFactory }>;
}

/** Per-sketch help: the gestures it responds to and a plain-language science note. */
export interface SketchHelp {
  /** One entry per distinct gesture: g = the gesture, e = what it does. */
  gestures: { g: string; e: string }[];
  /** Paragraphs of explanation, separated by blank lines. */
  science: string;
}

/**
 * Convenience base class. Register disposables with `track()` and they are
 * freed automatically — a sketch physically cannot leak a tracked resource.
 */
export abstract class BaseSketch implements Sketch {
  protected ctx!: SketchContext;
  private disposables: Array<{ dispose(): void }> = [];

  protected track<T extends { dispose(): void }>(resource: T): T {
    this.disposables.push(resource);
    return resource;
  }

  async init(ctx: SketchContext): Promise<void> {
    this.ctx = ctx;
    await this.setup();
  }

  /** Build scene/materials here. */
  protected abstract setup(): void | Promise<void>;

  abstract update(dt: number, t: number): void;
  abstract render(): void;

  resize(_w: number, _h: number): void {
    /* optional override */
  }

  dispose(): void {
    for (const d of this.disposables.splice(0)) {
      try {
        d.dispose();
      } catch {
        /* keep tearing down */
      }
    }
    this.teardown();
  }

  /** Extra cleanup beyond tracked disposables. */
  protected teardown(): void {
    /* optional override */
  }
}
